// PipelinePage — the pipeline board ('#pipeline'), BUILD-BRIEF §4 + the mockup.
//
// Seven columns by POSITION (lead → won·90d), never by hue. A stage move is always an event:
// `moveStage` appends one and updates `stage_entered_at`; there is no way from this page to
// edit `stage` directly. Lost deals keep their reason and a revisit date; the events zone is
// the append-only trail, latest first, and nothing here ever rewrites a row in it.
import { useCallback, useEffect, useMemo, useState } from 'react';
import EmptyState from '../components/EmptyState.js';
import { Att, Mast, PageShell, Rail, RailCell, Ref, Sheet, Tag, Tbl, Zone, ZoneHead } from '../components/PageKit.js';
import { usePoll } from '../components/usePoll.js';
import { useToast } from '../components/Toast.js';
import { useLocale, useT } from '../i18n/index.js';
import { request } from '../api/client.js';
import {
  createOpportunity, getOpportunity, getPipelineSummary, listOpportunities, listStageEvents, moveStage,
  type OpportunityEventView, type PipelineSummaryExt,
} from '../api/opportunities.js';
import { handover as sendHandover, previewHandover } from '../api/signet.js';
import { addMember, coverage as fetchCoverage, removeMember, listMembers as listBuyingGroupMembers } from '../api/buyingGroups.js';
import { listPersons } from '../api/persons.js';
import { open as openRoute, ROUTES } from '../routes.js';
import {
  BUYING_ROLES, OPEN_STAGES, STALE_DAYS,
  type BuyingGroupMember, type BuyingRole, type Coverage, type HandoverStatus, type ListResponse,
  type Organisation, type OpportunityCard, type OpportunityDetail, type OpportunityEvent, type Person,
  type SignetHandoverPayload, type Stage, type Stance,
} from '../../shared/types.js';
import './PipelinePage.de.js';
import './PipelinePage.fr.js';

/** `opportunities.handover_status` (§11) isn't on the shared `Opportunity` type yet — the row
 *  carries it (a migration column, `SELECT *`), the type just hasn't caught up. Read it through
 *  this narrow cast rather than widening the shared type from a page file. */
function handoverStatusOf(deal: OpportunityCard): HandoverStatus | null {
  return (deal as unknown as { handover_status?: HandoverStatus | null }).handover_status ?? null;
}

const COLUMN_STAGES: Stage[] = [...OPEN_STAGES, 'won'];
const STAGE_NUMBER: Record<Stage, number> = {
  lead: 1, qualified: 2, scoping: 3, proposal: 4, negotiation: 5, contracting: 6, won: 7, lost: 8,
};
const STAGE_LABEL: Record<Stage, string> = {
  lead: 'Lead', qualified: 'Qualified', scoping: 'Scoping', proposal: 'Proposal',
  negotiation: 'Negotiation', contracting: 'Contracting', won: 'Won', lost: 'Lost',
};
const WON_WINDOW_DAYS = 90;
const LOST_WINDOW_DAYS = 90;

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

/** '85’000' → '85k'. Only the pipe-h tally is abbreviated; every figure inside a card is exact. */
function abbrev(n: number): string {
  if (n >= 1000) return `${Math.round(n / 1000)}k`;
  return String(Math.round(n));
}

export default function PipelinePage() {
  const t = useT();
  const { fmtDate, fmtDateTime, fmtMoney } = useLocale();
  const toast = useToast();

  const fetchCards = useCallback(() => listOpportunities({}), []);
  const fetchSummary = useCallback(() => getPipelineSummary(), []);
  const fetchEvents = useCallback(() => listStageEvents(20), []);

  const cardsPoll = usePoll(fetchCards, 30_000);
  const summaryPoll = usePoll(fetchSummary, 30_000);
  const eventsPoll = usePoll(fetchEvents, 30_000);

  const refreshAll = useCallback(() => {
    void cardsPoll.refresh();
    void summaryPoll.refresh();
    void eventsPoll.refresh();
  }, [cardsPoll, summaryPoll, eventsPoll]);

  const cards = cardsPoll.data?.items ?? [];
  const summary = summaryPoll.data;
  const events = eventsPoll.data?.items ?? [];

  const byColumn = useMemo(() => {
    const map: Record<Stage, OpportunityCard[]> = {
      lead: [], qualified: [], scoping: [], proposal: [], negotiation: [], contracting: [], won: [], lost: [],
    };
    for (const c of cards) {
      if (c.stage === 'won') {
        if (daysSince(c.stage_entered_at) <= WON_WINDOW_DAYS) map.won.push(c);
      } else {
        map[c.stage]?.push(c);
      }
    }
    return map;
  }, [cards]);

  const openTotal = OPEN_STAGES.reduce((n, s) => n + (byColumn[s]?.length ?? 0), 0);

  const lost90 = useMemo(
    () => cards.filter((c) => c.stage === 'lost' && daysSince(c.stage_entered_at) <= LOST_WINDOW_DAYS),
    [cards],
  );

  const staleRefs = useMemo(
    () => cards.filter((c) => c.stale).map((c) => c.ref),
    [cards],
  );

  const [newDealOpen, setNewDealOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState<OpportunityCard | null>(null);
  const [handoverTarget, setHandoverTarget] = useState<OpportunityCard | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [detailTick, setDetailTick] = useState(0);

  // Buying group coverage (§10) — one figure per card, shown only once a group exists (`roles_filled
  // > 0`); a deal nobody has mapped yet stays silent rather than showing a noisy "0/6" on every
  // card. There is no batched endpoint (the route is one deal at a time), so this asks for each
  // visible card's coverage in parallel and keeps whatever answers land — a card whose request
  // is still in flight or failed simply shows nothing, same as "not collected".
  const [coverageByDeal, setCoverageByDeal] = useState<Record<number, Coverage>>({});
  useEffect(() => {
    let alive = true;
    Promise.all(cards.map((c) => fetchCoverage(c.id).then((cov) => [c.id, cov] as const).catch(() => null)))
      .then((pairs) => {
        if (!alive) return;
        const next: Record<number, Coverage> = {};
        for (const pair of pairs) if (pair) next[pair[0]] = pair[1];
        setCoverageByDeal(next);
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardsPoll.data, detailTick]);

  if (cardsPoll.error && !cardsPoll.data) {
    return (
      <PageShell>
        <Mast title={t('Pipeline')} />
        <EmptyState kind="error" title={t('Could not load the pipeline')} detail={cardsPoll.error} onRetry={refreshAll} />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Mast
        title={t('Pipeline')}
        sub={<>
          {t('seven stages')}<span className="sep">·</span>{t('a stage change is an event, never an overwrite')}
          <span className="sep">·</span>{t('a deal without a next step is a fault, not a style')}
        </>}
        verbs={<button type="button" className="btn btn-accent" onClick={() => setNewDealOpen(true)}>{t('New deal')}</button>}
      />

      <Rail>
        <RailCell
          label={t('Weighted')}
          value={summary ? fmtMoney(summary.weighted) : t('not collected')}
          tone={summary ? 'ink' : 'unknown'}
          detail={t('stage probability × face value')}
        />
        <RailCell
          label={t('Face value')}
          value={summary ? fmtMoney(summary.face_value) : t('not collected')}
          tone={summary ? 'ink' : 'unknown'}
          detail={t('{n} open deals', { n: String(openTotal) })}
        />
        <RailCell
          label={t('Stale deals')}
          value={summary ? summary.stale : t('not collected')}
          tone={summary ? (summary.stale > 0 ? 'attend' : 'ink') : 'unknown'}
          detail={staleRefs.length ? t('past {d}d in stage · {refs}', { d: String(STALE_DAYS), refs: staleRefs.join(' · ') }) : t('past {d}d in stage', { d: String(STALE_DAYS) })}
        />
        <RailCell
          label={t('Cycle · 12m')}
          value={summary?.cycle_days_median ?? t('not collected')}
          unit={summary?.cycle_days_median != null ? t('days median') : undefined}
          tone={summary?.cycle_days_median != null ? 'ink' : 'unknown'}
          detail={summary ? t('lead to signature · {n} deals', { n: String(summary.cycle_deals_12m) }) : t('lead to signature')}
        />
        <RailCell
          label={t('Win rate · 12m')}
          value={summary?.win_rate_12m ?? t('not collected')}
          unit={summary?.win_rate_12m != null ? '%' : undefined}
          tone={summary?.win_rate_12m != null ? 'ink' : 'unknown'}
          detail={summary ? t('{w} won · {l} lost · loss reasons recorded', { w: String(summary.won_12m), l: String(summary.lost_12m) }) : t('no won or lost deals in 12 months')}
        />
      </Rail>

      <Zone>
        <ZoneHead label={t('Open deals')} count={openTotal} note={t('stage is position, left to right')} />
        <Sheet clip className="pipe">
          {COLUMN_STAGES.map((stage) => {
            const deals = byColumn[stage] ?? [];
            const sum = deals.reduce((n, d) => n + (d.face_value == null ? 0 : Number(d.face_value)), 0);
            return (
              <div className={`pipe-col${stage === 'contracting' ? ' pipe-col--hold' : ''}`} key={stage}>
                <div className="pipe-h">
                  <span className="u-label">{STAGE_NUMBER[stage]} · {t(STAGE_LABEL[stage])}{stage === 'won' ? ` · ${WON_WINDOW_DAYS}d` : ''}</span>
                  <span className="n">{deals.length}{sum > 0 ? ` · ${abbrev(sum)}` : ''}</span>
                </div>
                {deals.length === 0 && <div className="d" style={{ padding: 'var(--s8) var(--s12)' }}>{t('No deals in this stage.')}</div>}
                {deals.map((deal) => (
                  <div className={`deal${deal.stale ? ' deal--stale' : ''}`} key={deal.id}>
                    <Ref>{deal.ref}</Ref>
                    <div className="t">
                      <button type="button" className="lnk" onClick={() => setDetailId(deal.id)}>{deal.title}</button>
                      {' '}<span className="sep">·</span> {deal.organisation_name}
                    </div>
                    <div className="d">
                      {deal.face_value != null ? <span className="voice-figure">{fmtMoney(deal.face_value, deal.currency)}</span> : <span className="n-none">—</span>}
                      {deal.probability != null && <> <span className="sep">·</span> {deal.probability}%</>}
                      {' '}<span className="sep">·</span>{' '}
                      {stage === 'won' ? (
                        <>{t('won on')} <span className="voice-figure">{fmtDate(deal.stage_entered_at)}</span></>
                      ) : (
                        <span style={deal.stale ? { color: 'var(--attend)' } : undefined}>
                          {t('{d}d', { d: String(deal.days_in_stage) })}
                          {deal.next_step ? <> <span className="sep">·</span> {t('next:')} {deal.next_step}{deal.next_step_due ? <> <span className="voice-figure">{fmtDate(deal.next_step_due)}</span></> : null}</> : <> <span className="sep">·</span> {t('no next step')}</>}
                        </span>
                      )}
                      {(() => {
                        const cov = coverageByDeal[deal.id];
                        if (!cov || cov.roles_filled === 0) return null;
                        return <> <span className="sep">·</span> {t('coverage')} <span className="voice-figure">{cov.roles_filled}/{cov.roles_total}</span></>;
                      })()}
                    </div>
                    <div className="d" style={{ marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      {stage === 'won' && <Tag tone="ok">{t(STAGE_LABEL[stage])}</Tag>}
                      {deal.signet_engagement_code && (
                        handoverStatusOf(deal) === 'failed' ? (
                          <Tag tone="attend"><Ref>{deal.signet_engagement_code}</Ref> {t('· handover failed')}</Tag>
                        ) : handoverStatusOf(deal) === 'pending' ? (
                          <Tag tone="attend"><Ref>{deal.signet_engagement_code}</Ref> {t('pending Signet')}</Tag>
                        ) : (
                          <Tag tone="ok"><Ref>{deal.signet_engagement_code}</Ref> {t('in Signet')}</Tag>
                        )
                      )}
                      {stage !== 'won' && (
                        <button type="button" className="lnk" onClick={() => setMoveTarget(deal)}>{t('Move stage →')}</button>
                      )}
                      {!deal.signet_engagement_code && (stage === 'contracting' || stage === 'won') && (
                        <button type="button" className="lnk" onClick={() => setHandoverTarget(deal)}>
                          {t('Hand over to Signet →')}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </Sheet>
      </Zone>

      <Zone>
        <ZoneHead label={t('Lost · 90d')} count={lost90.length} note={t('every loss carries a reason and a revisit date')} />
        <Sheet clip>
          {lost90.length === 0 && (
            <div className="row"><span className="d">{t('No deal has been lost in the last 90 days.')}</span></div>
          )}
          {lost90.map((deal) => (
            <div className="row" style={{ gridTemplateColumns: '1fr 130px 200px' }} key={deal.id}>
              <span>
                <Ref>{deal.ref}</Ref> <span className="w">{deal.title} <span className="sep">·</span> {deal.organisation_name}</span><br />
                <span className="d">
                  {t('lost on')} <span className="voice-figure">{fmtDate(deal.stage_entered_at)}</span>
                  <span className="sep">·</span> {t('reason:')} {deal.loss_reason ?? t('not recorded')}
                </span>
              </span>
              <span className="voice-figure">{deal.face_value != null ? fmtMoney(deal.face_value, deal.currency) : '—'}</span>
              <span className="d">
                {deal.revisit_on
                  ? <>{t('revisit')} <span className="voice-figure">{fmtDate(deal.revisit_on)}</span></>
                  : t('no revisit date set')}
                {' '}<button type="button" className="lnk" onClick={() => toast('ok', t('Commitments are not built yet — the interactions agent owns that page.'))}>{t('Create task')}</button>
              </span>
            </div>
          ))}
        </Sheet>
      </Zone>

      <Zone>
        <ZoneHead label={t('Stage events · latest')} count={t('append-only')} />
        <Sheet clip>
          {events.length === 0 && (
            <div className="row"><span className="d">{t('No stage has moved yet.')}</span></div>
          )}
          {events.map((ev) => (
            <div className="row" style={{ gridTemplateColumns: '140px 1fr auto' }} key={ev.id}>
              <span className="voice-figure d">{fmtDateTime(ev.created_at)}</span>
              <span>
                <Ref>{ev.opportunity_ref}</Ref>{' '}
                {ev.from_stage ? `${t(STAGE_LABEL[ev.from_stage])} → ${t(STAGE_LABEL[ev.to_stage])}` : t('opened at {stage}', { stage: t(STAGE_LABEL[ev.to_stage]) })}
                {ev.reason && <span className="d"> <span className="sep">·</span> {ev.reason}</span>}
                {ev.quote && <span className="d"> <span className="sep">·</span> <Att kind="quoted" /> «{ev.quote}»</span>}
              </span>
              <span className="d">
                {ev.days_in_previous_stage != null && ev.from_stage
                  ? t('{d}d in {stage}', { d: String(ev.days_in_previous_stage), stage: t(STAGE_LABEL[ev.from_stage]) })
                  : ' '}
              </span>
            </div>
          ))}
        </Sheet>
      </Zone>

      {newDealOpen && (
        <NewDealModal
          onClose={() => setNewDealOpen(false)}
          onCreated={() => { setNewDealOpen(false); refreshAll(); }}
        />
      )}
      {moveTarget && (
        <StageMoveModal
          deal={moveTarget}
          onClose={() => setMoveTarget(null)}
          onMoved={() => { setMoveTarget(null); refreshAll(); setDetailTick((n) => n + 1); }}
        />
      )}
      {detailId != null && (
        <DealDetailPanel
          key={`${detailId}-${detailTick}`}
          id={detailId}
          onClose={() => setDetailId(null)}
          onMove={(deal) => setMoveTarget(deal)}
        />
      )}
      {handoverTarget && (
        <HandoverModal
          deal={handoverTarget}
          onClose={() => setHandoverTarget(null)}
          onHandedOver={() => { setHandoverTarget(null); refreshAll(); setDetailTick((n) => n + 1); }}
        />
      )}
    </PageShell>
  );
}

// ── deal detail — slide-in panel ────────────────────────────────────────
// BUILD-BRIEF §4: "Detail = brief pages for person/organisation, slide-in panel elsewhere."
// A deal has no page of its own — this reads GET /api/opportunities/:id (row + its full
// append-only events trail, not just the board's latest-20 feed) into the design system's
// standard panel-overlay/panel-slide, same shape as every other slide-in detail in the app.
function DealDetailPanel({ id, onClose, onMove }: { id: number; onClose: () => void; onMove: (deal: OpportunityCard) => void }) {
  const t = useT();
  const { fmtDate, fmtDateTime, fmtMoney } = useLocale();
  const [detail, setDetail] = useState<OpportunityDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setDetail(null);
    setError(null);
    getOpportunity(id)
      .then((d) => { if (alive) setDetail(d); })
      .catch((err) => { if (alive) setError(err instanceof Error ? err.message : String(err)); });
    return () => { alive = false; };
  }, [id]);

  const deal = detail?.opportunity ?? null;

  return (
    <div className="panel-overlay open" onClick={onClose}>
      <div className="panel-overlay-bg" />
      <div className="panel-slide open" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="panel-slide-head">
          <h2 className="panel-slide-title">
            {deal ? <><Ref>{deal.ref}</Ref> {deal.title}</> : t('Deal')}
          </h2>
          <button type="button" className="panel-slide-close" onClick={onClose} aria-label={t('Close')}>×</button>
        </div>
        <div className="panel-slide-body">
          {error && <p className="d" style={{ color: 'var(--broken)' }}>{error}</p>}
          {!deal && !error && <p className="d">{t('Loading…')}</p>}
          {deal && (
            <>
              <div className="panel-section">
                <div className="u-label mb-8">{deal.organisation_name}</div>
                <div className="d">
                  {t(STAGE_LABEL[deal.stage])}
                  <span className="sep">·</span>
                  {deal.face_value != null ? <span className="voice-figure">{fmtMoney(deal.face_value, deal.currency)}</span> : <span className="n-none">—</span>}
                  {deal.probability != null && <> <span className="sep">·</span> {deal.probability}%</>}
                </div>
              </div>
              <div className="panel-section">
                <div className="u-label mb-8">{t('Next step')}</div>
                <div className="d">
                  {deal.next_step
                    ? <>{deal.next_step}{deal.next_step_due ? <> <span className="sep">·</span> <span className="voice-figure">{fmtDate(deal.next_step_due)}</span></> : null}</>
                    : t('no next step')}
                </div>
              </div>
              {deal.stage === 'lost' && (
                <div className="panel-section">
                  <div className="u-label mb-8">{t('Loss reason')}</div>
                  <div className="d">
                    {deal.loss_reason ?? t('not recorded')}
                    {deal.revisit_on && <> <span className="sep">·</span> {t('revisit')} <span className="voice-figure">{fmtDate(deal.revisit_on)}</span></>}
                  </div>
                </div>
              )}
              {deal.signet_engagement_code && (
                <div className="panel-section">
                  <div className="u-label mb-8">{t('Signet')}</div>
                  <div className="d"><Ref>{deal.signet_engagement_code}</Ref></div>
                </div>
              )}
              {deal.stage !== 'won' && deal.stage !== 'lost' && (
                <div className="panel-section">
                  <button type="button" className="btn btn-sm" onClick={() => onMove(deal)}>{t('Move stage →')}</button>
                </div>
              )}
              <BuyingGroupSection opportunityId={deal.id} />
              <div className="panel-section">
                <div className="u-label mb-8">{t('Stage events · latest')}</div>
                {detail!.events.length === 0 && <p className="d n-none">{t('No stage has moved yet.')}</p>}
                {detail!.events.map((ev: OpportunityEvent) => (
                  <div className="d" style={{ marginBottom: 6 }} key={ev.id}>
                    <span className="voice-figure">{fmtDateTime(ev.created_at)}</span>
                    <span className="sep">·</span>{' '}
                    {ev.from_stage ? `${t(STAGE_LABEL[ev.from_stage])} → ${t(STAGE_LABEL[ev.to_stage])}` : t('opened at {stage}', { stage: t(STAGE_LABEL[ev.to_stage]) })}
                    {ev.reason && <> <span className="sep">·</span> {ev.reason}</>}
                    {ev.quote && <> <span className="sep">·</span> <Att kind="quoted" /> «{ev.quote}»</>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── a plain search-as-you-type organisation picker ─────────────────────────
// Every deal belongs to an organisation, and the operator names it by typing, not by hunting a
// long select. The organisations route (§4) is another agent's; this asks for nothing beyond
// its documented `?q=` contract.
function OrgPicker({ value, onChange }: { value: Organisation | null; onChange: (o: Organisation | null) => void }) {
  const t = useT();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Organisation[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!q.trim() || value) { setResults([]); return; }
    let alive = true;
    const timer = setTimeout(() => {
      request<ListResponse<Organisation>>(`/organisations?q=${encodeURIComponent(q.trim())}`)
        .then((r) => { if (alive) setResults(r.items.slice(0, 8)); })
        .catch(() => { if (alive) setResults([]); });
    }, 200);
    return () => { alive = false; clearTimeout(timer); };
  }, [q, value]);

  if (value) {
    return (
      <div className="form-input" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{value.name}</span>
        <button type="button" className="lnk" onClick={() => onChange(null)}>{t('Change')}</button>
      </div>
    );
  }
  return (
    <div style={{ position: 'relative' }}>
      <input
        className="form-input"
        placeholder={t('Type to search organisations…')}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && results.length > 0 && (
        <div style={{
          position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, marginTop: 2,
          background: 'var(--surface)', border: '1px solid var(--line-strong)', borderRadius: 'var(--r1)',
          boxShadow: 'var(--elev-2)', maxHeight: 220, overflowY: 'auto',
        }}
        >
          {results.map((o) => (
            <button
              key={o.id}
              type="button"
              className="lnk"
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px' }}
              onMouseDown={() => { onChange(o); setQ(''); setResults([]); setOpen(false); }}
            >
              {o.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NewDealModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const t = useT();
  const toast = useToast();
  const [org, setOrg] = useState<Organisation | null>(null);
  const [title, setTitle] = useState('');
  const [faceValue, setFaceValue] = useState('');
  const [probability, setProbability] = useState('');
  const [nextStep, setNextStep] = useState('');
  const [nextStepDue, setNextStepDue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave = org != null && title.trim().length > 0 && !saving;

  const save = async () => {
    if (!org || !title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await createOpportunity({
        organisation_id: org.id,
        title: title.trim(),
        face_value: faceValue.trim() ? Number(faceValue) : null,
        probability: probability.trim() ? Number(probability) : null,
        next_step: nextStep.trim() || null,
        next_step_due: nextStepDue || null,
      });
      toast('ok', t('Deal created.'));
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{t('New deal')}</h2>
          <button className="close-btn" onClick={onClose} aria-label={t('Close')}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">{t('Organisation')}</label>
            <OrgPicker value={org} onChange={setOrg} />
          </div>
          <div className="form-group">
            <label className="form-label">{t('Title')}</label>
            <input className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('What is being sold')} />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">{t('Face value (CHF)')}</label>
              <input className="form-input" type="number" min="0" value={faceValue} onChange={(e) => setFaceValue(e.target.value)} />
            </div>
            <div className="form-group" style={{ width: 140 }}>
              <label className="form-label">{t('Probability %')}</label>
              <input className="form-input" type="number" min="0" max="100" value={probability} onChange={(e) => setProbability(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">{t('Next step')}</label>
            <input className="form-input" value={nextStep} onChange={(e) => setNextStep(e.target.value)} placeholder={t('A deal without a next step is a fault')} />
          </div>
          <div className="form-group">
            <label className="form-label">{t('Next step due')}</label>
            <input className="form-input" type="date" value={nextStepDue} onChange={(e) => setNextStepDue(e.target.value)} />
          </div>
          {error && <p className="d" style={{ color: 'var(--broken)' }}>{error}</p>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>{t('Cancel')}</button>
          <button className="btn btn-accent" disabled={!canSave} onClick={save}>{saving ? t('Saving…') : t('Save')}</button>
        </div>
      </div>
    </div>
  );
}

const NEXT_STAGE_OPTIONS: Stage[] = ['lead', 'qualified', 'scoping', 'proposal', 'negotiation', 'contracting', 'won', 'lost'];

function StageMoveModal({ deal, onClose, onMoved }: { deal: OpportunityCard; onClose: () => void; onMoved: () => void }) {
  const t = useT();
  const toast = useToast();
  const options = NEXT_STAGE_OPTIONS.filter((s) => s !== deal.stage);
  const [toStage, setToStage] = useState<Stage>(options[0]);
  const [reason, setReason] = useState('');
  const [quote, setQuote] = useState('');
  const [revisitOn, setRevisitOn] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave = reason.trim().length > 0 && !saving;

  const save = async () => {
    if (!reason.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await moveStage(deal.id, {
        to_stage: toStage,
        reason: reason.trim(),
        quote: quote.trim() || null,
        revisit_on: toStage === 'lost' && revisitOn ? revisitOn : null,
      });
      toast('ok', t('{ref} moved to {stage}.', { ref: deal.ref, stage: t(STAGE_LABEL[toStage]) }));
      onMoved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{t('Move stage')} <span className="d"><Ref>{deal.ref}</Ref> {deal.title}</span></h2>
          <button className="close-btn" onClick={onClose} aria-label={t('Close')}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">{t('From')} → {t('To stage')}</label>
            <div className="d" style={{ marginBottom: 6 }}>{t(STAGE_LABEL[deal.stage])} →</div>
            <select className="form-select" value={toStage} onChange={(e) => setToStage(e.target.value as Stage)}>
              {options.map((s) => <option key={s} value={s}>{t(STAGE_LABEL[s])}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{t('Reason')}</label>
            <textarea className="form-textarea" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder={toStage === 'lost' ? t('Why the deal was lost — this becomes the loss reason') : t('Why this move, in one line')}
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('Quote')} <span className="d">({t('optional')})</span></label>
            <textarea className="form-textarea" value={quote} onChange={(e) => setQuote(e.target.value)} placeholder={t('A verbatim line from the mail or the call, if there is one')} />
          </div>
          {toStage === 'lost' && (
            <div className="form-group">
              <label className="form-label">{t('Revisit on')} <span className="d">({t('optional')})</span></label>
              <input className="form-input" type="date" value={revisitOn} onChange={(e) => setRevisitOn(e.target.value)} />
            </div>
          )}
          {error && <p className="d" style={{ color: 'var(--broken)' }}>{error}</p>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>{t('Cancel')}</button>
          <button className="btn btn-accent" disabled={!canSave} onClick={save}>{saving ? t('Saving…') : t('Move')}</button>
        </div>
      </div>
    </div>
  );
}

// ── Signet handover — confirm modal (BUILD-BRIEF-R2 §11) ────────────────────
// Round 1 left this a toast saying "not built yet"; round 2 replaces it with the real thing:
// fetch the exact payload the operator's click will send, let them read it, then send it.
// `mode: 'stub'` is an honest answer, not a failure — the deal is left "pending Signet" until
// the live endpoint exists, and the operator sees that here before they even confirm.
function HandoverModal({ deal, onClose, onHandedOver }: {
  deal: OpportunityCard; onClose: () => void; onHandedOver: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const { fmtMoney, fmtDate } = useLocale();
  const [payload, setPayload] = useState<SignetHandoverPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let alive = true;
    previewHandover(deal.id)
      .then((p) => { if (alive) setPayload(p); })
      .catch((err) => { if (alive) setError(err instanceof Error ? err.message : String(err)); });
    return () => { alive = false; };
  }, [deal.id]);

  const confirm = async () => {
    setSending(true);
    setError(null);
    try {
      const result = await sendHandover(deal.id);
      if (result.status === 'failed') {
        setError(result.error ?? t('Signet refused the handover.'));
        return;
      }
      toast('ok', result.mode === 'live'
        ? t('{ref} handed over to Signet as {code}.', { ref: deal.ref, code: result.code ?? '' })
        : t('{ref} written to the Signet outbox — pending until Signet is configured.', { ref: deal.ref }));
      onHandedOver();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{t('Hand over to Signet')} <span className="d"><Ref>{deal.ref}</Ref> {deal.title}</span></h2>
          <button className="close-btn" onClick={onClose} aria-label={t('Close')}>×</button>
        </div>
        <div className="modal-body">
          {!payload && !error && <p className="d">{t('Loading…')}</p>}
          {error && <p className="d" style={{ color: 'var(--broken)' }}>{error}</p>}
          {payload && (
            <>
              <div className="form-group">
                <label className="form-label">{t('Client')}</label>
                <div className="d">
                  {payload.client.name}
                  {payload.client.sector && <> <span className="sep">·</span> {payload.client.sector}</>}
                  {payload.client.address && <> <span className="sep">·</span> {payload.client.address}</>}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('Contacts')}</label>
                {payload.contacts.length === 0 && <div className="d n-none">{t('No contact will be sent — the deal has no buying group and no primary person.')}</div>}
                {payload.contacts.map((c) => (
                  <div className="d" key={c.crm_person_ref}>
                    <Ref>{c.crm_person_ref}</Ref>{' '}
                    {[c.first_name, c.last_name].filter(Boolean).join(' ') || t('(no name)')}
                    {c.role_title && <> <span className="sep">·</span> {c.role_title}</>}
                    {c.is_sponsor && <> <span className="sep">·</span> <Tag tone="ok">{t('sponsor')}</Tag></>}
                    {c.email && <> <span className="sep">·</span> {c.email}</>}
                  </div>
                ))}
              </div>
              <div className="form-group">
                <label className="form-label">{t('Engagement')}</label>
                <div className="d">
                  {payload.engagement.name}
                  {payload.engagement.estimate_value != null && (
                    <> <span className="sep">·</span> <span className="voice-figure">{fmtMoney(payload.engagement.estimate_value, payload.engagement.currency)}</span></>
                  )}
                  {payload.engagement.kickoff_date && (
                    <> <span className="sep">·</span> {t('kickoff')} <span className="voice-figure">{fmtDate(payload.engagement.kickoff_date)}</span></>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>{t('Cancel')}</button>
          <button className="btn btn-accent" disabled={!payload || sending} onClick={confirm}>
            {sending ? t('Sending…') : t('Hand over')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Buying group — who has to say yes on this deal (BUILD-BRIEF-R2 §10) ─────
// The operator's own judgement about their own deal: no proposal, no claim mark. Coverage is
// arithmetic — the figure it feeds carries none either.
const ALL_BUYING_ROLES: BuyingRole[] = [...BUYING_ROLES, 'other'];
const ROLE_LABEL: Record<BuyingRole, string> = {
  sponsor: 'Sponsor', budget_owner: 'Budget owner', it_owner: 'IT owner', user: 'User',
  procurement: 'Procurement', data_protection: 'Data protection', other: 'Other',
};
const STANCE_LABEL: Record<Stance, string> = {
  unknown: 'Unknown', champion: 'Champion', neutral: 'Neutral', blocker: 'Blocker',
};
const STANCE_OPTIONS: Stance[] = ['unknown', 'champion', 'neutral', 'blocker'];

function BuyingGroupSection({ opportunityId }: { opportunityId: number }) {
  const t = useT();
  const toast = useToast();
  const [members, setMembers] = useState<BuyingGroupMember[]>([]);
  const [cov, setCov] = useState<Coverage | null>(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [addRole, setAddRole] = useState<BuyingRole>('sponsor');
  const [addStance, setAddStance] = useState<Stance>('unknown');
  const [addPerson, setAddPerson] = useState<{ id: number; ref: string; name: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([listBuyingGroupMembers(opportunityId), fetchCoverage(opportunityId)])
      .then(([m, c]) => { setMembers(m.items); setCov(c); })
      .catch((err) => toast('err', err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  }, [opportunityId, toast]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!addPerson) return;
    setBusy(true);
    try {
      await addMember({ opportunity_id: opportunityId, person_id: addPerson.id, role: addRole, stance: addStance });
      setAddPerson(null);
      setAddRole('sponsor');
      setAddStance('unknown');
      setAdding(false);
      load();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m: BuyingGroupMember) => {
    setBusy(true);
    try {
      await removeMember(m.id);
      load();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel-section">
      <div className="u-label mb-8">
        {t('Buying group')}
        {cov && cov.roles_filled > 0 && <> <span className="sep">·</span> <span className="voice-figure">{cov.roles_filled}/{cov.roles_total}</span></>}
      </div>
      {loading && <p className="d">{t('Loading…')}</p>}
      {!loading && members.length === 0 && <p className="d n-none">{t('No buying group mapped yet.')}</p>}
      {!loading && members.length > 0 && (
        <Tbl>
          <thead>
            <tr>
              <th>{t('Role')}</th>
              <th>{t('Stance')}</th>
              <th>{t('Person')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id}>
                <td>{t(ROLE_LABEL[m.role])}</td>
                <td><Tag tone={m.stance === 'champion' ? 'ok' : m.stance === 'blocker' ? 'broken' : 'neutral'}>{t(STANCE_LABEL[m.stance])}</Tag></td>
                <td>
                  <button type="button" className="lnk" onClick={() => openRoute(ROUTES.person({ id: m.person_id }))}>
                    {m.person_name ?? `#${m.person_id}`}
                  </button>
                  {m.person_ref && <> <Ref>{m.person_ref}</Ref></>}
                </td>
                <td>
                  <button type="button" className="lnk" disabled={busy} onClick={() => remove(m)}>{t('Remove')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </Tbl>
      )}
      {!adding && <button type="button" className="lnk" onClick={() => setAdding(true)}>{t('Add member →')}</button>}
      {adding && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <select className="form-select" value={addRole} onChange={(e) => setAddRole(e.target.value as BuyingRole)}>
            {ALL_BUYING_ROLES.map((r) => <option key={r} value={r}>{t(ROLE_LABEL[r])}</option>)}
          </select>
          <select className="form-select" value={addStance} onChange={(e) => setAddStance(e.target.value as Stance)}>
            {STANCE_OPTIONS.map((s) => <option key={s} value={s}>{t(STANCE_LABEL[s])}</option>)}
          </select>
          <PersonPicker value={addPerson} onChange={setAddPerson} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-sm btn-accent" disabled={!addPerson || busy} onClick={add}>{t('Add')}</button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => { setAdding(false); setAddPerson(null); }}>{t('Cancel')}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** A plain search-as-you-type person picker, same shape as `OrgPicker` above. */
function PersonPicker({ value, onChange }: {
  value: { id: number; ref: string; name: string } | null;
  onChange: (p: { id: number; ref: string; name: string } | null) => void;
}) {
  const t = useT();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Person[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!q.trim() || value) { setResults([]); return; }
    let alive = true;
    const timer = setTimeout(() => {
      listPersons({ q: q.trim(), limit: 8 })
        .then((r) => { if (alive) setResults(r.items); })
        .catch(() => { if (alive) setResults([]); });
    }, 200);
    return () => { alive = false; clearTimeout(timer); };
  }, [q, value]);

  if (value) {
    return (
      <div className="form-input" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{value.name}</span>
        <button type="button" className="lnk" onClick={() => onChange(null)}>{t('Change')}</button>
      </div>
    );
  }
  return (
    <div style={{ position: 'relative' }}>
      <input
        className="form-input"
        placeholder={t('Type to search people…')}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && results.length > 0 && (
        <div style={{
          position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, marginTop: 2,
          background: 'var(--surface)', border: '1px solid var(--line-strong)', borderRadius: 'var(--r1)',
          boxShadow: 'var(--elev-2)', maxHeight: 220, overflowY: 'auto',
        }}
        >
          {results.map((p) => (
            <button
              key={p.id}
              type="button"
              className="lnk"
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px' }}
              onMouseDown={() => { onChange({ id: p.id, ref: p.ref, name: p.name }); setQ(''); setResults([]); setOpen(false); }}
            >
              {p.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
